import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
// @ts-ignore
import mapboxgl from "mapbox-gl/dist/mapbox-gl-csp";
import ZoomOutMapIcon from "@material-ui/icons/ZoomOutMap";
import ImageModalWrapper from "./ImageModalWrapper";
import { RootState } from "../../../store";
import {
  hoverPeak,
  clickPeak,
  setZoomPeak
} from "../../../store/selection/actions";
import { ParsedRecord, Image } from "../../../types/airtable";
import { getGpxCoordinates } from "../../../utils/gpx";

import stateGeoJson from "../../../assets/us-states.json";
import "./Map.scss";

// @ts-ignore
import MapboxWorker from "worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker"; // Load worker code separately with worker-loader
mapboxgl.workerClass = MapboxWorker;

// const MAP_STYLE_URL = "mapbox://styles/mdp8989/ckmjpym5p126v17lrmc18mf2y";
// const MAP_STYLE_URL = "mapbox://styles/mapbox/streets-v11";
const MAP_STYLE_SATELLITE = "mapbox://styles/mapbox/satellite-streets-v11";
const MAP_STYLE_URL =
  "mapbox://styles/mapbox-map-design/ckhqrf2tz0dt119ny6azh975y";
const STATE_OUTLINES_DATA_KEYNAME = "name";

function formatString(template: string, args: (string | number)[]) {
  let i = 0;
  return template.replace(/{}/g, () => {
    return typeof args[i] != "undefined" ? args[i++].toString() : "";
  });
}

const popupHtmlTemplate = `
<div class="popup-peak-header">
  <span class="popup-peak-name">{}</span>
  <span class="popup-peak-abbrev">&nbsp({})</span>
</div>
<div class="popup-peak-elevation">{}'</div>
`;

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

function usePrevious(value: string | null): string | null {
  const ref = useRef<string | null>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current || null;
}

const Map = ({ records }: { records: ParsedRecord[] }) => {
  const mapContainerRef = useRef(null);
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const prevHoveredState = usePrevious(hoveredState);

  // redux hooks
  const hoveredPeak = useSelector(
    (state: RootState) => state.selection.selection.hoveredPeak
  );
  const zoomedPeak = useSelector(
    (state: RootState) => state.selection.selection.zoomedPeak
  );
  const hoveredImage = useSelector(
    (state: RootState) => state.selection.selection.hoveredImage
  );
  const dispatch = useDispatch();

  // Store references for map markers
  const [peakMarkers, setPeakMarkers] = useState<{
    [peakId: string]: mapboxgl.Marker;
  }>({});
  const [photoMarkers, setPhotoMarkers] = useState<{
    [imageId: string]: mapboxgl.Marker;
  }>({});

  // Reference values for resetting the map
  let initMapZoom = 0;
  let initMapCenter = [0, 0];

  // Track each zoom operation to determine style transitions
  let zoomStart = 0;
  let zoomCurrent = 0;
  const TERRAIN_THRESHOLD = 7;

  // Initialize map when component mounts
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current || "",
      style: MAP_STYLE_URL,
      bounds: [
        [-128.352252, 22.395798],
        [-61.630719, 51.703208]
      ],
      doubleClickZoom: false
    });

    map.on(
      // @ts-ignore
      "load",
      () => {
        // Initialize starting center and zoom level for use in Reset function
        initMapZoom = map.getZoom();
        initMapCenter = map.getCenter().toArray();

        // STATE BOUNDARIES AND FILLS
        map.addSource("states", {
          type: "geojson",
          data: JSON.parse(JSON.stringify(stateGeoJson))
        });

        // Build a GL match expression that defines the color for every vector tile feature
        // Use the state name as the lookup key for the state shape
        // const matchExpression = ["match", ["get", "US_State_Boundaries_-_5m"]];
        const matchExpression: mapboxgl.Expression = [
          "match",
          ["get", STATE_OUTLINES_DATA_KEYNAME]
        ];

        // Calculate color values for each state based on a record data value
        records.forEach((record) => {
          if (record.SUMMIT_DATE) {
            // Convert the range of data values to a suitable color
            const color = "rgba(41, 226, 41, 0.5)";
            matchExpression.push(record.STATE, color);
          }
        });
        // Last value is the default, used where there is no data
        matchExpression.push("rgba(0, 0, 0, 0)");

        // Combine match expression with a boolean case that first checks a feature's 'hover' property
        const caseExpression: mapboxgl.Expression = [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          "rgba(107, 183, 255, 0.5)",
          matchExpression
        ];

        map.addLayer({
          id: "states-fills",
          type: "fill",
          source: "states",
          maxzoom: TERRAIN_THRESHOLD,
          paint: {
            // @ts-ignore
            "fill-color": caseExpression,
            "fill-outline-color": "rgb(167, 167, 167)"
          }
        });

        /*
        ██   ██ ██  ██████  ██   ██ ██████   ██████  ██ ███    ██ ████████     ███    ███  █████  ██████  ██   ██ ███████ ██████  ███████ 
        ██   ██ ██ ██       ██   ██ ██   ██ ██    ██ ██ ████   ██    ██        ████  ████ ██   ██ ██   ██ ██  ██  ██      ██   ██ ██      
        ███████ ██ ██   ███ ███████ ██████  ██    ██ ██ ██ ██  ██    ██        ██ ████ ██ ███████ ██████  █████   █████   ██████  ███████ 
        ██   ██ ██ ██    ██ ██   ██ ██      ██    ██ ██ ██  ██ ██    ██        ██  ██  ██ ██   ██ ██   ██ ██  ██  ██      ██   ██      ██ 
        ██   ██ ██  ██████  ██   ██ ██       ██████  ██ ██   ████    ██        ██      ██ ██   ██ ██   ██ ██   ██ ███████ ██   ██ ███████ 
        */
        const highpointFeatures = records.map((record) => {
          return {
            type: "Feature",
            properties: {
              peakId: record.PEAK_ID,
              name: record.NAME,
              state: record.STATE,
              stateCode: record.STATE_CODE,
              elevation: record.ELEVATION,
              summitDate: record.SUMMIT_DATE
            },
            geometry: {
              type: "Point",
              coordinates: [record.LONGITUDE, record.LATITUDE]
            }
          };
        });

        const peakMarkersTemp: { [peakId: string]: mapboxgl.Marker } = {};

        highpointFeatures.forEach((marker) => {
          const el = document.createElement("div");
          el.className = "marker";
          if (marker.properties.summitDate) {
            el.className += " completed";
          }
          const mbMarker = new mapboxgl.Marker(el)
            // @ts-ignore
            .setLngLat(marker.geometry.coordinates)
            .setPopup(
              new mapboxgl.Popup({ offset: 10, closeButton: false }).setHTML(
                formatString(popupHtmlTemplate, [
                  marker.properties.name,
                  marker.properties.stateCode,
                  marker.properties.elevation
                ])
              )
            )
            .addTo(map);

          // add to temp collection
          peakMarkersTemp[marker.properties.peakId] = mbMarker;

          // listen for click events to trigger Redux and flyTo actions
          el.addEventListener("click", () => {
            dispatch(clickPeak(marker.properties.peakId));
          });

          el.addEventListener("dblclick", () =>
            dispatch(setZoomPeak(marker.properties.peakId))
          );
        });

        // Set markers to state variable
        setPeakMarkers(peakMarkersTemp);
        /*
        ████████ ███████ ██████  ██████   █████  ██ ███    ██ 
           ██    ██      ██   ██ ██   ██ ██   ██ ██ ████   ██ 
           ██    █████   ██████  ██████  ███████ ██ ██ ██  ██ 
           ██    ██      ██   ██ ██   ██ ██   ██ ██ ██  ██ ██ 
           ██    ███████ ██   ██ ██   ██ ██   ██ ██ ██   ████ 
        */
        map.addSource("mapbox-dem", {
          type: "raster-dem",
          url: "mapbox://mapbox.mapbox-terrain-dem-v1",
          tileSize: 512,
          minzoom: TERRAIN_THRESHOLD
        });

        // add a sky layer that will show when the map is highly pitched
        map.addLayer({
          id: "sky",
          type: "sky",
          paint: {
            "sky-type": "atmosphere",
            "sky-atmosphere-sun": [0.0, 0.0],
            "sky-atmosphere-sun-intensity": 15
          }
        });

        setMap(map);
      }
    );

    map.on("zoomstart", () => {
      zoomStart = map.getZoom();
    });

    map.on("zoom", () => {
      const zoom = map.getZoom();
      if (
        zoom > zoomStart &&
        zoomCurrent < TERRAIN_THRESHOLD &&
        zoom >= TERRAIN_THRESHOLD
      ) {
        // Enable terrain
        map.setTerrain({ source: "mapbox-dem", exaggeration: 1.0 });
        // Disable peak markers
        for (const peakId in peakMarkers) {
          // const marker = peakMarkers[peakId];
          // console.log(marker);
          // console.log(`removing ${peakId}`);
          // marker.remove();
          // const element = marker.getElement();
          // console.log(element);
          // element?.parentNode?.removeChild(element);
          // map.removeLayer(marker);
        }
        console.log(peakMarkers);

        // // switch to satellite style
        // map.setStyle(MAP_STYLE_SATELLITE, {diff: false});
        // // add the DEM source as a terrain layer with exaggerated height
        // map.setTerrain({ source: "mapbox-dem", exaggeration: 1.5 });
        // // add a sky layer that will show when the map is highly pitched
        // map.addLayer({
        //   id: "sky",
        //   type: "sky",
        //   paint: {
        //     "sky-type": "atmosphere",
        //     "sky-atmosphere-sun": [0.0, 0.0],
        //     "sky-atmosphere-sun-intensity": 15
        //   }
        // });
      } else if (
        zoom < zoomStart &&
        zoomCurrent > TERRAIN_THRESHOLD &&
        zoom <= TERRAIN_THRESHOLD
      ) {
        // Disable terrain
        map.setTerrain(null);

        // Enable peak markers
        for (const peakId in peakMarkers) {
          const marker = peakMarkers[peakId];
          // marker.addTo(map);
        }

        // // switch to base style
        // map.setStyle(MAP_STYLE_URL);
        // // remove terrain/sky
        // map.setTerrain(null);
        // map.removeLayer("sky");
      }
      zoomCurrent = zoom;
    });

    map.on(
      "mousemove",
      "states-fills",
      function (e: mapboxgl.MapLayerMouseEvent) {
        if (e.features?.length === 1) {
          const hoveredStateName =
            e.features![0].properties?.[STATE_OUTLINES_DATA_KEYNAME];
          // Unhighlight previous state features if different from currently hovered state
          if (hoveredState !== hoveredStateName) {
            setHoveredState(hoveredStateName);
          }
        }
      }
    );

    map.on("mouseenter", "states-fills", function () {
      map.getCanvas().style.cursor = "pointer";
    });

    // When the mouse leaves the state-fill layer, update the feature state of the
    // previously hovered feature.
    map.on("mouseleave", "states-fills", function () {
      map.getCanvas().style.cursor = "";
      setHoveredState(null);
      dispatch(hoverPeak(null));
    });

    map.on("click", "states-fills", function (e: mapboxgl.MapLayerMouseEvent) {
      if (e.features?.length === 1) {
        const hoveredStateName =
          e.features![0].properties?.[STATE_OUTLINES_DATA_KEYNAME];
        console.log(hoveredStateName);
        if (!hoveredStateName) return;
        const peakId = records.find(
          (r) => r.STATE === hoveredStateName
        )?.PEAK_ID;
        if (peakId) {
          dispatch(clickPeak(peakId));
        }
      }
    });

    map.on(
      "dblclick",
      "states-fills",
      function (e: mapboxgl.MapLayerMouseEvent) {
        if (e.features?.length === 1) {
          const hoveredStateName =
            e.features![0].properties?.[STATE_OUTLINES_DATA_KEYNAME];
          if (!hoveredStateName) return;
          const peakId = records.find(
            (r) => r.STATE === hoveredStateName
          )?.PEAK_ID;
          if (peakId) {
            dispatch(setZoomPeak(peakId));
          }
        }
      }
    );

    // Attach event listener to the reset button
    document.getElementById("reset-zoom-btn")?.addEventListener("click", () => {
      resetMap(map);
    });

    // Clean up on unmount
    return () => map.remove();
  }, []);

  const getFeaturesByState = (map: mapboxgl.Map, stateName: string | null) => {
    if (!stateName) return [];
    return map.querySourceFeatures("states", {
      filter: ["==", STATE_OUTLINES_DATA_KEYNAME, stateName]
    });
  };

  const setFeaturesHoverState = (
    map: mapboxgl.Map,
    features: mapboxgl.MapboxGeoJSONFeature[],
    hoverState: boolean
  ) => {
    features.forEach((feature) => {
      map.setFeatureState(
        {
          source: "states",
          id: feature.id
        },
        { hover: hoverState }
      );
    });
  };

  useEffect(() => {
    setStateFills(hoveredState, prevHoveredState);
    const peakId = records.find((r) => r.STATE === hoveredState)?.PEAK_ID;
    if (peakId) {
      dispatch(hoverPeak(peakId));
    }
  }, [hoveredState, prevHoveredState]);

  const setStateFills = (
    hoveredState: string | null,
    prevHoveredState: string | null
  ) => {
    if (map) {
      if (hoveredState === prevHoveredState) return;
      // console.log(hoveredState, prevHoveredState);
      let featuresToHighlight: mapboxgl.MapboxGeoJSONFeature[];
      if (hoveredState) {
        featuresToHighlight = getFeaturesByState(map, hoveredState);
        setFeaturesHoverState(map, featuresToHighlight, true);
      }
      if (prevHoveredState) {
        const featuresToUnhighlight = getFeaturesByState(map, prevHoveredState);
        setFeaturesHoverState(map, featuresToUnhighlight, false);
      }
    }
  };

  const zoomToPeak = async (map: mapboxgl.Map, peakId: string) => {
    const record = records.find((r) => r.PEAK_ID === peakId);
    if (!record) return;

    // Add GPX track, if one exists
    if (record.HIKING_GPX) {
      const gpxData = await getGpxCoordinates(record.HIKING_GPX);
      const srcData = {
        type: "geojson",
        data: gpxData.features[0]
      };
      const existingGpxSrc = map.getSource("gpx-track");
      if (existingGpxSrc) {
        // @ts-ignore
        existingGpxSrc.setData(srcData.data);
      } else {
        // @ts-ignore
        map.addSource("gpx-track", srcData);
        map.addLayer({
          id: "gpx-track",
          type: "line",
          source: "gpx-track",
          layout: {
            "line-join": "round",
            "line-cap": "round"
          },
          paint: {
            "line-color": "rgb(255,0,0)",
            "line-width": 8
          },
          minzoom: TERRAIN_THRESHOLD
        });
      }
    }

    // TODO: Add layer with photo markers

    // Fly/zoom into peak
    map.flyTo({
      center: [record.LONGITUDE, record.LATITUDE],
      zoom: 14,
      bearing: 0,
      pitch: 78,
      speed: 0.99,
      curve: 1,
      essential: true,
      easing: function (t: number) {
        return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      }
    });
  };

  const resetMap = (map: mapboxgl.Map) => {
    dispatch(setZoomPeak(null));
    map.flyTo({
      // @ts-ignore
      center: initMapCenter,
      zoom: initMapZoom,
      bearing: 0,
      pitch: 0,
      speed: 0.99,
      curve: 1,
      essential: true,
      easing: function (t: number) {
        return 1 - (1 - t) * (1 - t);
      }
    });
  };

  // show marker tooltip popup when side panel card hovered
  useEffect(() => {
    if (hoveredPeak) {
      showPeakMarkerPopup(hoveredPeak);
    } else {
      hidePeakMarkerPopups();
    }
  }, [hoveredPeak]);

  const showPeakMarkerPopup = (peakId: string) => {
    hidePeakMarkerPopups();
    const marker = peakMarkers[peakId];
    if (!marker) return;
    marker.togglePopup();
  };

  const hidePeakMarkerPopups = () => {
    for (const peakId in peakMarkers) {
      const marker = peakMarkers[peakId];
      if (marker.getPopup().isOpen()) {
        marker.togglePopup();
      }
    }
  };

  /*
  ██████  ██   ██  ██████  ████████  ██████      ███    ███  █████  ██████  ██   ██ ███████ ██████  ███████ 
  ██   ██ ██   ██ ██    ██    ██    ██    ██     ████  ████ ██   ██ ██   ██ ██  ██  ██      ██   ██ ██      
  ██████  ███████ ██    ██    ██    ██    ██     ██ ████ ██ ███████ ██████  █████   █████   ██████  ███████ 
  ██      ██   ██ ██    ██    ██    ██    ██     ██  ██  ██ ██   ██ ██   ██ ██  ██  ██      ██   ██      ██ 
  ██      ██   ██  ██████     ██     ██████      ██      ██ ██   ██ ██   ██ ██   ██ ███████ ██   ██ ███████ 
  */

  useEffect(() => {
    shrinkPhotoMarkers();
    if (hoveredImage) {
      enlargePhotoMarker(hoveredImage);
    }
  }, [hoveredImage]);

  useEffect(() => {
    if (!map) return;
    if (zoomedPeak) {
      zoomToPeak(map, zoomedPeak);
      addPhotoMarkers(zoomedPeak);
    } else {
      removePhotoMarkers();
    }
  }, [zoomedPeak]);

  const addPhotoMarkers = (peakId: string) => {
    removePhotoMarkers();

    if (!map) return;

    const record = records.find((r) => r.PEAK_ID === peakId);
    if (!record) return;

    const photoFeatures = record.IMAGES.map((image) => {
      return {
        type: "Feature",
        properties: {
          image,
          imageId: image.id,
          thumbnailUrl: image.thumbnailUrls.small
        },
        geometry: {
          type: "Point",
          coordinates: [image.longitude, image.latitude]
        }
      };
    });

    const photoMarkersTemp: { [imageId: string]: mapboxgl.Marker } = {};

    photoFeatures.forEach((marker) => {
      const el = document.createElement("div");
      el.className = "photo-marker";
      el.style.backgroundImage = `url('${marker.properties.thumbnailUrl}')`;
      const mbMarker = new mapboxgl.Marker(el, {
        anchor: "bottom",
        offset: [0, -30]
      })
        // @ts-ignore
        .setLngLat(marker.geometry.coordinates)
        // .setPopup(
        //   new mapboxgl.Popup({ offset: 10, closeButton: false }).setHTML(
        //     formatString(popupHtmlTemplate, [
        //       marker.properties.name,
        //       marker.properties.stateCode,
        //       marker.properties.elevation
        //     ])
        //   )
        // )
        .addTo(map);

      // add to temp collection
      photoMarkersTemp[marker.properties.imageId] = mbMarker;

      // listen for click events to trigger image modal
      el.addEventListener("click", () => {
        setModalImage(marker.properties.image);
      });
    });

    // Set markers to state variable
    setPhotoMarkers(photoMarkersTemp);
  };

  const removePhotoMarkers = () => {
    for (const imageId in photoMarkers) {
      const marker = photoMarkers[imageId];
      marker.remove();
    }
  };

  const enlargePhotoMarker = (imageId: string) => {
    if (!imageId) return;
    const marker = photoMarkers[imageId];
    if (!marker) return;
    const el = marker.getElement();
    el.className += " enlarged";
  };

  const shrinkPhotoMarkers = () => {
    for (const imageId in photoMarkers) {
      const el = photoMarkers[imageId].getElement();
      el.classList.remove("enlarged");
    }
  };

  // Modal state
  const [modalImage, setModalImage] = useState<Image | null>(null);
  const modalCloseCallback = () => {
    setModalImage(null);
  };

  return (
    <div ref={mapContainerRef} id="map-container">
      <div id="reset-zoom-btn" aria-label="zoom-out">
        <ZoomOutMapIcon />
      </div>
      <ImageModalWrapper image={modalImage} onClose={modalCloseCallback} />
    </div>
  );
};

export default Map;
